import { Component, OnInit, Input, ViewChild, Inject, Output, EventEmitter } from '@angular/core';
import { Sim, SimWithUsage } from '../../models/sim';
import { TranslateService } from '@ngx-translate/core';
import { SimDataService } from '../../services/sim-data.service';
import { ConfirmationDialogService } from '../../../../shared/services/confirmation-dialog.service';
import { SimEditComponent } from '../sim-edit/sim-edit.component';
import { Operator } from '../../models/operator';
import { Pool } from '../../../pools/models/pool';
import { Train } from '../../../trains/models/train';
import { MessageService } from 'primeng/api';
import { SimConsumptionPeriodSelectorComponent } from '../sim-consumption-period-selector/sim-consumption-period-selector.component';
import { Project } from '../../../project/models/project';
import {Subscription} from "rxjs";
import { TableStateService } from '../../../../shared/services/table-state.service';
@Component({
  selector: 'app-sim-list',
  template: require('./sim-list.component.html'),
  styles: [require('./sim-list.component.sass')]
})
export class SimListComponent implements OnInit {

  @ViewChild(SimEditComponent) simEdit: SimEditComponent;
  @ViewChild(SimConsumptionPeriodSelectorComponent) simConsumptionPeriodExport: SimConsumptionPeriodSelectorComponent;

  @Input() projectId: number;
  @Input() sims: Sim[] | SimWithUsage[];
  @Input() isConfiguration: boolean;
  @Input() isOverview: boolean;
  @Input() projects: Project[];
  @Input() trains: Train[];
  @Input() operators: Operator[];
  @Input() pools: Pool[];
  @Input() simTableKey: string;

  @Output() viewSim = new EventEmitter<Sim | SimWithUsage>();
  @Output() refresh = new EventEmitter();

  tableConfiguration: any = {
    paginate: true,
    sortableMode: 'multiple',
    defaultPagination: 250,
    itemsPerPageOptions: [50, 100, 250, 500],
    globalFilterFields: ['iccid', 'trainName', 'boxId', 'link', 'simConfig.simId', 'simConfig.msisdn', 'simConfig.operator', 'simConfig.pool'],
    exportFilename: 'sims'
  }

  columns: any[] ;

  languageChangeSubscription: Subscription;

  constructor(
      private simDataService: SimDataService,
      @Inject('ProjectService') private projectService: any,
      @Inject('AuthenticationFactory') private authenticationFactory: any,
      private confirmationDialogService: ConfirmationDialogService,
      private translate: TranslateService,
      private messageService: MessageService,
      public tableState: TableStateService
  ) {
    this.languageChangeSubscription = this.translate.onLangChange.subscribe(() => {
      this.refreshSimTable();
    })
  }

  ngOnInit(): void {
    this.refreshSimTable()
  }

  refreshSimTable = () => {
    this.columns = [{
      header: this.translate.instant('I18N_SIM.ICCID'),
      field: 'iccid',
      type: 'text',
      width: '200px'
    },
      {
        header: this.translate.instant('I18N_SIM.TRAIN'),
        field: 'trainName',
        type: 'name',
        width: '150px',
        hidden: !(this.isConfiguration || this.isOverview)
      },
      {
        header: this.translate.instant('I18N_SIM.CCU'),
        field: 'boxId',
        type: 'box',
      },
      {
        header: this.translate.instant('I18N_SIM.MODEM'),
        field: 'link',
        type: 'modem',
      }, {
        header: this.translate.instant('I18N_SIM.SIM_SLOT'),
        field: 'simConfig.simId',
        type: 'slot',
      }, {
        header: this.translate.instant('I18N_SIM.MSISDN'),
        field: 'simConfig.msisdn',
        type: 'text',
        isConfiguration: true
      }, {
        header: this.translate.instant('I18N_SIM.START_DATE'),
        field: 'simConfig.startDate',
        isConfiguration: true
      }, {
        header: this.translate.instant('I18N_SIM.LAST_SEEN'),
        field: 'lastSeen'
      }, {
        header: this.translate.instant('I18N_SIM.IN_USE'),
        field: 'inUse'
      }, {
        header: this.translate.instant('I18N_SIM.USAGE_MB'),
        hidden: this.isConfiguration,
        width: '150px',
        field: 'usage',
      }, {
        header: this.translate.instant('I18N_SIM.PERIOD'),
        hidden: this.isConfiguration,
        width: '200px',
        field: 'period',
        disableSorting: true,
      }, {
        header: this.translate.instant('I18N_SIM.LIMIT_MB'),
        field: 'simConfig.limit',
      },
      {
        header: this.translate.instant('I18N_SIM.POOL'),
        field: 'simConfig.pool',
        type:  'pool',
        isConfiguration: true
      }, {
        header: this.translate.instant('I18N_SIM.OPERATOR'),
        field: 'simConfig.operator',
        type: 'operator',
        width: '150px',
      },
      {
        header: this.translate.instant('I18N_SIM.PRICE_BEFORE_OVER_AFTER'),
        field: 'simConfig.price',
        isConfiguration: true
      }, {
        header: this.translate.instant('I18N_SIM.NATIONAL'),
        field: 'simConfig.national',
        type: 'national',
        isConfiguration: true
      },
      {
        header: this.translate.instant('I18N_GENERAL.ACTIONS'),
        disableSorting: true,
        disableSearch: true,
        field: 'actions',
        hidden: !(this.isConfiguration || this.isOverview)
      }];
    this.columns = this.columns.filter(column => !this.isConfiguration ? !column.isConfiguration : true)
  }


  async addSim() {
    await this.simEdit.showModal();
  }

  async editSim(sim: Sim) {
    await this.simEdit.showModal(sim)
  }

  onSaveSim = async (sim: Sim) => {
    try {
      if (sim && sim.id) {
        await this.simDataService.updateSim(sim, this.projectId);
      } else {
        await this.simDataService.createSim(sim, this.projectId);
      }
      this.messageService.add({
        severity:'success',
        summary: this.translate.instant(sim && sim.id ? "I18N_SIM.EDIT_SIM": "I18N_SIM.CREATE_SIM"),
        detail:`SIM ${this.translate.instant(sim && sim.id ? "I18N_GENERAL.HAS_BEEN_SUCCESSFULLY_UPDATED" : "I18N_GENERAL.HAS_BEEN_SUCCESSFULLY_CREATED")} `
      });
      this.refresh.emit();
    } catch (error) {
      this.messageService.add({
        severity:'error',
        summary: this.translate.instant(sim && sim.id ? "I18N_SIM.EDIT_SIM": "I18N_SIM.CREATE_SIM"),
        detail: error && error.message
      });
    }

  }

  deleteSim = async (sim: Sim) => {
    if (sim && sim.id) {
      const confirmed = await this.confirmationDialogService.confirm(
          `${this.translate.instant('I18N_SIM.DELETE_SIM')} ${sim.iccid}`,
          this.translate.instant('I18N_SIM.ARE_YOU_SURE_DELETE'),
          'danger',
          this.translate.instant('I18N_SIM.DELETE_SIM'),
          this.translate.instant('I18N_GENERAL.CANCEL'),
          'lg');
      if (confirmed) {
        try {
          await this.simDataService.deleteSim(sim.iccid, this.projectId);
          this.messageService.add({
            severity:'success',
            summary: this.translate.instant('I18N_SIM.DELETE_SIM'),
            detail:`SIM ${this.translate.instant("I18N_GENERAL.HAS_BEEN_SUCCESSFULLY_DELETED")}`
          });
          this.refresh.emit();
        } catch (error) {
          this.messageService.add({
            severity:'error',
            summary: this.translate.instant('I18N_SIM.DELETE_SIM'),
            detail: error && error.message
          });
        }
      }
    }
  }


  onViewSim = (sim: Sim | SimWithUsage) => {
    this.viewSim.emit(sim);
  }

  openPeriodExportSelection = () => {
    this.simConsumptionPeriodExport.showModal();
  }


  onSelectExportPeriod = (data: {
    dateFrom: string, 
    dateTo: string
  }) => {
    if (data) {
      const token = this.authenticationFactory.getUser() && this.authenticationFactory.getUser().token;
      this.simDataService.exportSimDataPerPeriod(this.projectId, data.dateFrom, data.dateTo, token);
    }
  }
}
